/**
 * Phase A of the printed-photo-book feature (see project plan) — pure,
 * read-only assembly of a book's page sequence from a guide's existing
 * "Personalize Guide with Places" embedded photos. No PDF, no vendor, no
 * payment here — this only answers "what photos, in what order, from
 * where" so a preview screen can render them.
 *
 * Source of photos: PlacesHeader.js's _buildEmbedHtml already writes
 * `<img data-photo-id="<rawUrl>">` tags into each Place's Info HTML — this
 * module parses those back out. There is no dedicated multi-photo field on
 * Place; this free-edited HTML is the only structured-enough source that
 * already exists.
 */

import { creditLine, creditNeeded, PRINT_MIN_PX } from "./photoCredit";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";
import { DateTime } from "luxon";
import { API_BASE_URL } from "./Common";
import { buildStaticMapUrl, formatDate, dayKey } from "./exportTripHTML";

// Short "Month Day – Month Day, Year" form for the cover caption —
// formatDate's own "Monday, September 14, 2026" is too long for an overlay.
// Compresses same-month/same-year ranges (e.g. "September 14–18, 2026");
// keeps both full dates for a cross-month or cross-year trip.
function formatCoverDateRange(dayLabels) {
  const keys = Array.from(dayLabels.keys()).sort();
  if (keys.length === 0) return "";
  const start = DateTime.fromISO(keys[0], { zone: "utc" });
  const end = DateTime.fromISO(keys[keys.length - 1], { zone: "utc" });
  if (!start.isValid || !end.isValid) return "";
  if (start.toISODate() === end.toISODate()) return start.toFormat("LLLL d, yyyy");
  if (start.year === end.year) {
    if (start.month === end.month) return `${start.toFormat("LLLL d")}–${end.toFormat("d, yyyy")}`;
    return `${start.toFormat("LLLL d")} – ${end.toFormat("LLLL d, yyyy")}`;
  }
  return `${start.toFormat("LLLL d, yyyy")} – ${end.toFormat("LLLL d, yyyy")}`;
}

// Physical-role label for a page at `index` within a PADDED (even-count)
// page array of `total` pages — matches Peecho's own hardcover binding
// diagram (see the purring-humming-mountain plan's leaf-mapping table):
// page 1 is always the outside front cover, the last page is always the
// outside back cover, everything between is an interior spread. Used by
// both the editable page list (unpadded — see assemblePhotoBookPages'
// caller) and the flip-viewer (padded, so a synthetic blank filler page
// correctly reads as "Back Cover (blank)" rather than being silently
// unlabeled).
export function pageRoleLabel(index, total) {
  if (total <= 0) return "";
  if (index === 0) return "Front Cover";
  if (index === total - 1) return "Back Cover";
  return `Page ${index + 1}`;
}

// Matches the exact tag shape _buildEmbedHtml/_buildPersonalizeEmbedHtml
// produce on both platforms: `<img ... data-photo-id="RAW_URL" ...>`.
// Deliberately tolerant of attribute order (style/alt can be anywhere)
// since this is scraping free-edited rich text, not a fixed schema — the
// user can reorder/retype attributes via the HTML editor.
const PHOTO_ID_RE = /<img\b[^>]*\bdata-photo-id="([^"]*)"[^>]*>/gi;

// One place's embedded photos, in the order they appear in its Info HTML.
// De-duplicated (a place could theoretically have the same url embedded
// twice via repeated manual edits) while preserving first-occurrence order.
export function extractEmbeddedPhotos(place) {
  const html = place?.Info || "";
  const seen = new Set();
  const urls = [];
  let m;
  PHOTO_ID_RE.lastIndex = 0;
  while ((m = PHOTO_ID_RE.exec(html)) !== null) {
    const rawUrl = m[1];
    if (!rawUrl || seen.has(rawUrl)) continue;
    seen.add(rawUrl);
    urls.push(rawUrl);
  }
  return urls;
}

// Legacy "no real photo" sentinel values Common.getImageURL also special-
// cases elsewhere — a place's cover Photo/Photo_m can hold one of these
// instead of being genuinely empty, and neither represents real content
// worth printing.
const PLACEHOLDER_PHOTO_URLS = new Set([
  "http://liquidguides.com/Media/Images/google-places-logo.jpg",
  "http://liquidguides.com/Media/Images/place.png",
]);

function isUsablePhotoUrl(url) {
  return !!url && !PLACEHOLDER_PHOTO_URLS.has(url);
}

// Editable captions + text-only pages (project plan) — no changes to
// Chapter/Section models, reuses them exactly as Sections.js/
// SectionsDialog.js already do elsewhere in the app. Customized pages get
// their own Section under one lazily-created "Photo Book" chapter; the
// Section's Title encodes a stable identity prefix (independent of any
// human-readable label that might change later, e.g. a renamed place) so a
// fresh assemblePhotoBookPages call can re-attach a previously-saved
// caption automatically. Full Title shape: `${identityKey} — ${label}`.
export const PHOTO_BOOK_CHAPTER_TITLE = "Photo Book";

export function photoBookIdentityKey(page) {
  // The designed front cover (4.10.55) keeps its own title, apart from the
  // same photo's caption inside the book.
  if (page.isCover) return "Cover";
  if (page.type === "photo" && page.place?.ID) return `Photo: ${page.place.ID}`;
  if (page.type === "map" && page.mapDayKey) return `Map: ${page.mapDayKey}`;
  return null; // text-only pages aren't auto-matched (session-only, per project plan)
}

export function photoBookSectionTitle(page, label) {
  const key = photoBookIdentityKey(page);
  return key ? `${key} — ${label}` : `Text: ${label}`;
}

// Read-only lookup — never creates the chapter/section itself (that only
// happens when the user actually edits a caption, see the Preview screens).
function findPhotoBookCaptionSection(guide, page) {
  const key = photoBookIdentityKey(page);
  if (!key) return null;
  const chapter = (guide?.chapters || []).find((c) => c.Title === PHOTO_BOOK_CHAPTER_TITLE);
  if (!chapter) return null;
  const section = (chapter.sections || []).find((s) => s.Title.startsWith(`${key} —`));
  return section ? { chapter, section } : null;
}

// Flat, ordered list of { place, rawUrl } — one entry per photo, which is
// one entry per book page for the v1 one-photo-per-page layout. Order:
// trip-chronological via the schedule (event Start order, already sorted
// at decode time — see Schedule.js), falling back to placesList order for
// any place that has embedded photos but was never scheduled.
//
// Per place: use its notes-embedded photos (from Personalize Guide with
// Photos) if any exist; otherwise fall back to its single cover
// Photo_m/Photo, so a place nobody's explicitly personalized yet still
// contributes something — most places already have a cover photo from
// stock-photo enrichment, while embedding extra photos is an opt-in step.
// Hardcover/Softcover/Layflat all require an EVEN page count (double-sided
// printing — odd → an unwanted white back cover per Peecho's own docs).
// functions/index.js's buildBookHtml already pads for this server-side
// before the real PDF is generated; the preview viewers (PhotoBookFlipViewer
// on both platforms) need the SAME padding applied to what they display,
// or an odd-count book's preview silently omits the trailing blank page
// that will actually print. Found 2026-10-02 (user asked how certain we
// could be the preview matches the real book).
// Peecho's square Hardcover Photobook prints 24–300 pages (production
// offering, functions/index.js peechoPageLimits); a shorter book is filled
// with blank pages and billed as 24.
export const PRINT_MIN_PAGES = 24;

// Blanks added by the "Add N blank pages" button to reach the minimum are
// tagged "blank:filler-…". Once other pages bring the book back above the
// minimum they're no longer needed, so the extra ones are dropped (last
// first), never going below the minimum. Returns the new pages array, or
// null when nothing changes (2026-10-20).
export const isFillerBlank = (p) => p.type === "blank" && String(p.blankId || "").startsWith("blank:filler-");
export function trimUnneededFillers(pages, includeMaps, minPages = PRINT_MIN_PAGES) {
  const visible = (p) => !p.excluded && (includeMaps || p.type !== "map");
  const visibleCount = pages.filter(visible).length;
  let extra = Math.min(pages.filter((p) => visible(p) && isFillerBlank(p)).length, visibleCount - minPages);
  if (extra <= 0) return null;
  const result = pages.slice();
  for (let i = result.length - 1; i >= 0 && extra > 0; i--) {
    if (visible(result[i]) && isFillerBlank(result[i])) {
      result.splice(i, 1);
      extra--;
    }
  }
  return result;
}

// The pages as they will print (same rule as functions/index.js's
// buildBookHtml):
//  - under PRINT_MIN_PAGES: blank pages go in just BEFORE the back cover
//    (the last page), so the chosen back cover stays the back cover
//    (2026-10-19 — they used to be added after it, giving a white back
//    cover and pushing the user's back page inside);
//  - otherwise an odd count gets one blank at the very end (a white back
//    cover — the page list says so and offers a blank inside instead),
//    except before a designed back cover (4.10.55), which stays the back
//    cover. The order sends these padded pages, so the print server (which
//    would add its blank at the end) has nothing left to add.
export function padPagesToEven(pages, minPages = PRINT_MIN_PAGES) {
  if (pages.length > 0 && pages.length < minPages) {
    const fill = Array.from({ length: minPages - pages.length }, () => ({ type: "blank", filler: true }));
    return pages.length === 1 ? [...pages, ...fill] : [...pages.slice(0, -1), ...fill, pages[pages.length - 1]];
  }
  if (pages.length % 2 === 0) return pages;
  if (pages[pages.length - 1]?.isBackCover) return [...pages.slice(0, -1), { type: "blank" }, pages[pages.length - 1]];
  return [...pages, { type: "blank" }];
}

// Strips HTML tags to plain text for anywhere a caption needs to show as
// plain text (Preview list rows, the mobile flip-viewer) — converting
// block-level boundaries to newlines first, then decoding the handful of
// entities the HTML editor actually produces. Previously just stripped
// tags (`replace(/<[^>]+>/g, " ")`) without decoding entities, so a caption
// built from pasted/indented text showed literal "&nbsp;" runs instead of
// spaces (confirmed 2026-10-02, both platforms' Preview list rows had this
// same bug independently). Shared here instead of reimplementing per call
// site, after fixing it twice separately already.
export function htmlToPlainText(html) {
  if (!html) return "";
  return html
    .replace(/<(p|div|br|li)\b[^>]*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

// ── Front and back covers (4.10.55, 2026-10-26) ─────────────────────────
// A new book starts with a designed front cover (a full-bleed photo with
// the guide name and dates — the first trip photo; the guide's own picture
// is often a small thumbnail, so it's only offered in the picker) and ends with a designed back cover (a smaller second trip photo,
// a one-line summary and "Made with Andiamo Places", which can be turned
// off). Both photos change with one tap from a grid of the book's photos
// (coverPhotoChoices). A book already arranged and saved before this keeps
// its own covers (applyPhotoBookLayout leaves these out of it).
export const COVER_KEY = "cover";
export const BACK_COVER_KEY = "backcover";
export const MADE_WITH_TEXT = "Made with Andiamo Places";

const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

// Every distinct photo the covers can use: the book's photo pages in order,
// then the guide's own picture. { rawUrl, printUrl, place }.
export function coverPhotoChoices(guide, pages) {
  const seen = new Set();
  const out = [];
  const add = (c) => {
    if (!c || !isUsablePhotoUrl(c.rawUrl) || seen.has(c.rawUrl)) return;
    seen.add(c.rawUrl);
    out.push(c);
  };
  (pages || []).forEach((p) => {
    if (p.type === "photo" && !p.isCover) add({ rawUrl: p.rawUrl, printUrl: p.printUrl || null, place: p.place || null });
  });
  const own = guide?.attributes?.image_m || guide?.attributes?.image;
  if (own) add({ rawUrl: own, printUrl: null, place: null });
  return out;
}

export function makeCoverPage(choice) {
  return { type: "photo", isCover: true, place: choice.place || null, rawUrl: choice.rawUrl, printUrl: choice.printUrl || null, objectPosition: "center" };
}

export function buildBackCoverHtml({ photoUrl, text, madeWith }) {
  const img = photoUrl ? `<p style="text-align:center;margin:0 0 18px"><img src="${escapeHtml(photoUrl)}" alt="" style="width:55%;height:auto;border-radius:6px"></p>` : "";
  const line = text ? `<p style="text-align:center;font-size:15px;margin:0">${escapeHtml(text)}</p>` : "";
  const brand = madeWith ? `<p style="text-align:center;font-size:9px;color:#888888;margin:36px 0 0">${MADE_WITH_TEXT}</p>` : "";
  return `${img}${line}${brand}`;
}

// photo: a coverPhotoChoices entry or null; text: the summary line.
export function makeBackCoverPage({ photo, text, madeWith = true }) {
  return {
    type: "html",
    isBackCover: true,
    title: "Back cover",
    backPhoto: photo || null,
    backText: text || "",
    madeWith: madeWith !== false,
    // Screen-size photo here; resolveAllPagesForPdf swaps in the print one.
    captionHtml: buildBackCoverHtml({ photoUrl: photo ? resolvePreviewImageSrc(photo.rawUrl) : "", text: text || "", madeWith: madeWith !== false }),
  };
}

// "Japan · 61 photos · June 19 – July 2, 2026" — the book's photos, not its places: in a journal
// every photo is a place of its own, so "61 places" was really 61 photos (2026-10-29).
export function backCoverSummary(guide, photoCount, dateRange) {
  const countries = (guide?.attributes?.countries || []).filter(Boolean);
  const parts = [];
  if (countries.length) parts.push(countries.slice(0, 3).join(", ") + (countries.length > 3 ? "…" : ""));
  if (photoCount > 0) parts.push(`${photoCount} photo${photoCount === 1 ? "" : "s"}`);
  if (dateRange) parts.push(dateRange);
  return parts.join(" · ");
}

export function assemblePhotoBookPages(guide) {
  const pages = [];
  const visitedPlaceIds = new Set();

  const pushPlacePhotos = (place) => {
    if (!place || visitedPlaceIds.has(place.ID)) return;
    visitedPlaceIds.add(place.ID);
    const urls = extractEmbeddedPhotos(place);
    if (urls.length > 0) {
      urls.forEach((rawUrl) => pages.push({ type: "photo", place, rawUrl, objectPosition: "center" }));
      return;
    }
    const coverUrl = place.Photo_m || place.Photo;
    // printUrl: a stock photo's largest version (Common/photoCredit.js) —
    // the cover is only screen size.
    if (isUsablePhotoUrl(coverUrl)) pages.push({ type: "photo", place, rawUrl: coverUrl, printUrl: place.PhotoPrint || null, objectPosition: "center" });
  };

  const events = (guide?.schedule?.events || []).slice();

  // One map page per scheduled day (project plan: "Map pages" section),
  // inserted ahead of that day's photos — reuses exportTripHTML.js's own
  // dayKey/formatDate/buildStaticMapUrl rather than re-deriving day
  // grouping or map-building logic. Square (640x640, the Static Maps API's
  // own per-side max) to match the book's own trim size and avoid the same
  // blind-center-crop risk the photo pages have their own fix for.
  const dayPlaces = new Map(); // dayKey -> Place[]
  const dayLabels = new Map(); // dayKey -> formatted label
  events.forEach((ev) => {
    if (!ev.Start) return;
    const key = dayKey(ev.Start);
    if (!dayLabels.has(key)) {
      dayLabels.set(key, formatDate(ev.Start));
      dayPlaces.set(key, []);
    }
    (ev.Places || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .forEach((id) => {
        const place = guide.places.getPlace(id);
        if (place) dayPlaces.get(key).push(place);
      });
  });

  const insertedDayKeys = new Set();
  events.forEach((ev) => {
    if (ev.Start) {
      const key = dayKey(ev.Start);
      if (!insertedDayKeys.has(key)) {
        insertedDayKeys.add(key);
        const imageUrl = buildStaticMapUrl(dayPlaces.get(key) || [], 640, 640);
        if (imageUrl)
          pages.push({ type: "map", imageUrl, caption: dayLabels.get(key), objectPosition: "center", mapDayKey: key });
      }
    }
    (ev.Places || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean)
      .forEach((id) => pushPlacePhotos(guide.places.getPlace(id)));
  });

  // Anything with embedded photos that was never scheduled — still part of
  // the guide, so still part of the book, just appended after the
  // chronological portion rather than interleaved arbitrarily.
  (guide?.places?.placesList || [])
    .filter((p) => !p.Hidden && p.PType !== "PolyLine" && p.PType !== "Polygon")
    .forEach(pushPlacePhotos);

  // Multi-destination overview map: one more map page, as the very first
  // page (ahead of even the first destination's own day map), showing every
  // place in the book together — the per-day maps above already cover a
  // single location well on their own, but a trip spanning multiple
  // destinations (guide.destinations, the structured field from the
  // Destinations-first feature) benefits from a "here's everywhere we went"
  // overview too, seen before any single destination's map. Built from the
  // same Place objects already collected into `pages` just above (not
  // destination-level coordinates, which don't exist as a field on
  // guide.destinations), so it's always accurate to what's actually in the
  // book. mapDayKey "overview" gives it a stable identity
  // (photoBookIdentityKey) so a custom caption on it persists across
  // sessions like any other page. Placing it at index 0 means it takes over
  // the front-cover slot (and the default guide-name/date-range cover
  // caption below applies to it, same as it would to whatever page 0
  // otherwise was) — moved here from index 1 (right after the cover) on
  // 2026-10-03 per explicit feedback that it read as appearing AFTER the
  // first destination's own map rather than before it.
  if ((guide?.destinations?.length || 0) > 1) {
    const bookPlaces = Array.from(
      new Map(pages.filter((p) => p.type === "photo" && p.place).map((p) => [p.place.ID, p.place])).values()
    );
    const overviewMapUrl = buildStaticMapUrl(bookPlaces, 640, 640);
    if (overviewMapUrl) {
      pages.splice(0, 0, {
        type: "map",
        imageUrl: overviewMapUrl,
        caption: "Trip Overview",
        objectPosition: "center",
        mapDayKey: "overview",
      });
    }
  }

  // Designed covers (4.10.55): front first, back last — added before the
  // captions are re-attached (the cover's own title is kept as "Cover") and
  // before the credits, which go in just before the back cover.
  {
    const choices = coverPhotoChoices(guide, pages);
    if (choices.length > 0) {
      const photoCount = pages.filter((p) => p.type === "photo").length;
      pages.unshift(makeCoverPage(choices[0]));
      pages.push(makeBackCoverPage({
        photo: choices[1] || choices[0],
        text: backCoverSummary(guide, photoCount, formatCoverDateRange(dayLabels)),
        madeWith: true,
      }));
    }
  }

  // Re-attach any previously-saved custom caption (project plan: editable
  // captions) — a fresh assembly otherwise has no memory of edits made in
  // an earlier session, since the rest of the page arrangement is
  // deliberately session-only; this is the one thing that's meant to stick.
  pages.forEach((page) => {
    const found = findPhotoBookCaptionSection(guide, page);
    if (found) {
      page.captionHtml = found.section.html;
      page.sectionId = found.section.ID;
      page.chapterId = found.chapter.ID;
    }
  });

  // Default front-cover title: page 1 is always the book's physical outside
  // front cover (see pageRoleLabel/Peecho's own binding diagram), so give it
  // a sensible default — the guide name + trip date range — instead of
  // leaving it as a bare, uncaptioned photo. Only when NOTHING has been
  // explicitly saved for it yet: captionHtml stays `undefined` here unless
  // findPhotoBookCaptionSection found a real (possibly empty) Section above,
  // so a user who opens the editor, clears this default, and saves an empty
  // caption has genuinely removed it for good — same persistence mechanism
  // as any other page's caption, nothing cover-specific to maintain.
  //
  // Not attached to pages[0] any more (2026-10-17): that stuck to the photo
  // that happened to be first, so after a custom cover page was added or
  // pages were moved, the guide-name title showed up on an inside page. It
  // travels on the array instead (pages.coverDefaultHtml) and the preview
  // applies it to whatever is the front cover — a photo or map with no
  // caption of its own (withCoverDefault).
  {
    const guideName = guide?.attributes?.guide_name;
    const dateRange = formatCoverDateRange(dayLabels);
    const escape = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    pages.coverDefaultHtml = guideName ? `<p>${escape(guideName)}</p>${dateRange ? `<p>${escape(dateRange)}</p>` : ""}` : null;
  }

  // Photo credits (2026-10-17): one text page listing who took the stock
  // photos in the book (Flickr / Wikimedia — Common/photoCredit.js), placed
  // just before the back cover. Rebuilt on every assembly, so it always
  // matches the photos in the book; it can be moved or removed like any
  // other page.
  // As many pages as the list needs — one printed page holds about 16
  // credits, and the real book cuts off whatever doesn't fit.
  const creditPages = buildCreditsPages(pages);
  if (creditPages.length > 0) {
    const at = pages.length >= 2 ? pages.length - 1 : pages.length;
    pages.splice(at, 0, ...creditPages.map((html, i) => ({
      type: "html",
      title: creditPages.length > 1 ? `Photo credits (${i + 1} of ${creditPages.length})` : "Photo credits",
      captionHtml: html,
      autoCredits: true,
      creditsIndex: i,
    })));
  }

  return pages;
}

// The credits pages' HTML (an empty array when no photo in the book has a
// credit), CREDITS_PER_PAGE to a page. Photos whose licence needs no credit
// (public domain, CC0) are listed too, as a courtesy, after the ones that do.
const CREDITS_PER_PAGE = 16;
export function buildCreditsPages(pages) {
  const escape = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const seen = new Set();
  const rows = [];
  pages.forEach((page) => {
    const credit = page.type === "photo" && page.place?.PhotoCredit;
    if (!credit || seen.has(page.place.ID)) return;
    seen.add(page.place.ID);
    rows.push({ name: page.place.Name || "", line: creditLine(credit), needed: creditNeeded(credit) });
  });
  if (rows.length === 0) return [];
  rows.sort((a, b) => Number(b.needed) - Number(a.needed));
  const chunks = [];
  for (let i = 0; i < rows.length; i += CREDITS_PER_PAGE) chunks.push(rows.slice(i, i + CREDITS_PER_PAGE));
  return chunks.map((chunk, i) => {
    const items = chunk.map((r) => `<li style="margin-bottom:4px"><b>${escape(r.name)}</b> — ${escape(r.line)}</li>`).join("");
    const heading = chunks.length > 1 ? `Photo credits (${i + 1} of ${chunks.length})` : "Photo credits";
    return `<h3>${heading}</h3><ul style="font-size:11px;line-height:1.35;padding-left:16px;margin:0">${items}</ul>`;
  });
}

// ── Saved arrangement (2026-10-17) ──────────────────────────────────────
// The preview used to forget everything but photo/map captions when it
// closed — page order, crops, pages left out, custom text pages and the
// maps choice were lost, a real problem when arranging a book to order.
// guide.photoBookLayout = { v: 1, includeMaps, pages: [entry] }, entry =
// { k: key, x: left out, pos: crop } plus, for a custom text page, title
// and html. Keys: "photo:<placeID>:<image>", "map:<day>", "credits",
// "blankcover", "blank:<id>" (a blank page inside), "text:<id>".
export function photoBookPageKey(page) {
  if (page.isCover) return COVER_KEY;
  if (page.isBackCover) return BACK_COVER_KEY;
  if (page.type === "photo") return `photo:${page.place?.ID ?? ""}:${page.rawUrl || ""}`;
  if (page.type === "map") return `map:${page.mapDayKey || page.caption || ""}`;
  if (page.type === "html" && page.autoCredits) return page.creditsIndex ? `credits:${page.creditsIndex + 1}` : "credits";
  if (page.type === "html") return page.textId || null;
  if (page.type === "blank" && page.manualCover) return "blankcover";
  if (page.type === "blank" && page.blankId) return page.blankId;
  return null;
}

export function photoBookLayoutFrom(pages, includeMaps) {
  const out = [];
  pages.forEach((p) => {
    const k = photoBookPageKey(p);
    if (!k) return;
    const e = { k };
    if (p.excluded) e.x = true;
    if (p.objectPosition && p.objectPosition !== "center") e.pos = p.objectPosition;
    // The covers' chosen photos, the back cover's text and "Made with".
    if (k === COVER_KEY) { e.src = p.rawUrl || ""; e.psrc = p.printUrl || ""; e.pid = p.place?.ID ?? ""; }
    if (k === BACK_COVER_KEY) {
      e.src = p.backPhoto?.rawUrl || "";
      e.psrc = p.backPhoto?.printUrl || "";
      e.pid = p.backPhoto?.place?.ID ?? "";
      e.text = p.backText || "";
      e.mw = p.madeWith !== false;
    }
    if (k.startsWith("text:")) {
      e.title = p.title || "";
      e.html = p.captionHtml || "";
      // The section holding its text ("Text: <title>" in the Photo Book
      // chapter), read back on restore so edits made there show.
      if (p.sectionId) { e.sid = p.sectionId; e.cid = p.chapterId; }
    }
    out.push(e);
  });
  return { v: 1, includeMaps: includeMaps !== false, pages: out };
}

// The freshly assembled pages arranged as saved: saved pages in their saved
// order (with crop and left-out state), custom pages restored, then pages
// that are new since (new photos) at the end. Pages whose photo or place is
// gone drop out. Returns { pages, includeMaps }.
export function applyPhotoBookLayout(fresh, layout, guide = null) {
  const sectionHtml = (e) => {
    if (!e.sid || !guide) return null;
    const chapter = (guide.chapters || []).find((c) => c.ID === e.cid) || (guide.chapters || []).find((c) => c.Title === PHOTO_BOOK_CHAPTER_TITLE);
    const section = chapter && (chapter.sections || []).find((x) => x.ID === e.sid);
    return section ? section.html : null;
  };
  if (!layout || !Array.isArray(layout.pages)) return { pages: fresh, includeMaps: true };
  const byKey = new Map();
  fresh.forEach((p) => { const k = photoBookPageKey(p); if (k && !byKey.has(k)) byKey.set(k, p); });
  const used = new Set();
  const pages = [];
  layout.pages.forEach((e) => {
    if (!e || !e.k) return;
    if (e.k.startsWith("text:")) {
      // Its section's text when it still exists (edited in Chapters/
      // Sections or not), else the copy kept with the layout.
      const fromSection = sectionHtml(e);
      pages.push({
        type: "html",
        textId: e.k,
        title: e.title || "",
        captionHtml: fromSection != null ? fromSection : e.html || "",
        excluded: !!e.x,
        ...(fromSection != null ? { sectionId: e.sid, chapterId: e.cid } : {}),
      });
      return;
    }
    if (e.k === "blankcover") { pages.push({ type: "blank", manualCover: true }); return; }
    if (e.k === COVER_KEY || e.k === BACK_COVER_KEY) {
      const p = byKey.get(e.k);
      if (!p || used.has(e.k)) return;
      used.add(e.k);
      const place = e.pid && guide?.places?.getPlace ? guide.places.getPlace(e.pid) || null : null;
      const photo = e.src ? { rawUrl: e.src, printUrl: e.psrc || null, place } : null;
      if (e.k === COVER_KEY) {
        pages.push({ ...p, ...(photo ? { rawUrl: photo.rawUrl, printUrl: photo.printUrl, place } : {}), objectPosition: e.pos || "center", excluded: !!e.x });
      } else {
        pages.push({ ...makeBackCoverPage({ photo, text: e.text != null ? e.text : p.backText, madeWith: e.mw !== false }), excluded: !!e.x });
      }
      return;
    }
    if (e.k.startsWith("blank:")) { pages.push({ type: "blank", blankId: e.k, excluded: !!e.x }); return; }
    const p = byKey.get(e.k);
    if (!p || used.has(e.k)) return;
    used.add(e.k);
    pages.push({ ...p, objectPosition: e.pos || p.objectPosition || "center", excluded: !!e.x });
  });
  const added = [];
  fresh.forEach((p) => {
    const k = photoBookPageKey(p);
    if (k && used.has(k)) return;
    // A book saved before the designed covers existed keeps its own.
    if (p.isCover || p.isBackCover) return;
    // An extra credits page (the list grew) goes next to the others, or
    // before the back cover — not after it.
    if (p.autoCredits) {
      let at = -1;
      pages.forEach((q, i) => { if (q.autoCredits) at = i + 1; });
      if (at === -1) at = pages.length >= 2 ? pages.length - 1 : pages.length;
      pages.splice(at, 0, p);
      return;
    }
    added.push(p);
  });
  // Photos (and day maps) added to the guide since the layout was saved go
  // in before the photo credits, or before the back cover when there are
  // none — at the very end they became the back cover and hid behind it
  // (2026-10-18).
  if (added.length) {
    let at = pages.findIndex((q) => q.autoCredits);
    if (at === -1) at = pages.length >= 2 ? pages.length - 1 : pages.length;
    pages.splice(at, 0, ...added);
  }
  return { pages, includeMaps: layout.includeMaps !== false };
}

// The book's pages with the default front-cover title (see
// assemblePhotoBookPages' coverDefaultHtml) applied to the front cover — the
// first page — when it's a photo or map with no caption of its own. A text
// page or a blank cover keeps what it has.
export function withCoverDefault(pages, coverDefaultHtml) {
  if (!coverDefaultHtml || pages.length === 0) return pages;
  const first = pages[0];
  if ((first.type !== "photo" && first.type !== "map") || first.captionHtml !== undefined) return pages;
  return [{ ...first, captionHtml: coverDefaultHtml }, ...pages.slice(1)];
}

// Resolves one page's rawUrl to the best available image source for print.
//
// Firebase-storage urls (https://...): ImageDialog.js's upload pipeline
// writes three tiers under one shared base filename — url/url_m (150px/
// 800px, deliberately small for upload speed and in-app list/map
// navigation speed) plus a third "_l" file (~2000px/quality 0.85) added
// only for print. Nothing persists which photos have an "_l" sibling —
// url/url_m's storage path predictably ends in "_t"/"_m" sharing one base
// name, so the "_l" path is derived by suffix substitution and PROBED for
// (firebase.storage().refFromURL + getDownloadURL, the same
// refFromURL(...) idiom ImageDialog.js's deleteFileMedium already uses).
// Falls back to rawUrl itself (url_m) for a photo uploaded before this
// third tier existed, where no "_l" file exists to find.
async function resolveFirebaseLargeVariant(rawUrl) {
  try {
    const ref = firebase.storage().refFromURL(rawUrl);
    const largePath = ref.fullPath.replace(/_m$/, "_l");
    if (largePath === ref.fullPath) return rawUrl; // url_m didn't end in _m — not this pipeline's shape
    const largeRef = firebase.storage().ref(largePath);
    return await largeRef.getDownloadURL();
  } catch (e) {
    return rawUrl; // no "_l" sibling (older photo) — url_m is the best we have
  }
}

// gdrive:{fileId} urls do NOT resolve through the app's normal in-place
// embed path (Common.getImageURL's `lh3.googleusercontent.com/d/{fileId}`)
// — that's an undocumented Google *thumbnail* endpoint, not a guaranteed
// full-resolution link. This fetches the real file bytes via the Drive API
// (`files/{id}?alt=media`) instead, using the same refresh-token exchange
// (gdrive_refresh.php) GoogleDrivePicker.js's own DriveImage/fetchBlob
// already does — reused here rather than invented fresh.
const DRIVE_REFRESH_TOKEN_KEY = "gdrive_refresh_token";

async function getDriveAccessToken() {
  const refreshToken = localStorage.getItem(DRIVE_REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;
  const res = await fetch(`${API_BASE_URL}/gdrive_refresh.php`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `refresh_token=${encodeURIComponent(refreshToken)}`,
  });
  if (!res.ok) return null;
  const data = await res.json();
  return data.access_token || null;
}

// Minimum acceptable print pixel dimension for the book's 210mm (8.27in)
// square trim at ~150 DPI — a commonly-used "good enough" floor for a photo
// book (300 DPI is the pristine target, but most consumer photo prints read
// fine well below that). 150 * 8.27in ≈ 1240px.
// PRINT_MIN_PX (1240) now lives in photoCredit.js, shared with the search
// results' size labels.

// Lightweight, metadata-only quality check — NOT a full download (unlike
// resolvePageImageSrc above, used only once an order actually starts).
// Called once per page when the Preview screen opens (see
// PhotoBookPreviewModal.js), not on every render, so the one-time
// Storage/Drive metadata call per photo is an acceptable cost. Firebase:
// checks for the existence of the "_l" print-tier file (a getMetadata()
// call, not getDownloadURL — cheaper, no need for the actual URL) — that
// tier is always ~2000px, well above PRINT_MIN_PX, so existence alone is
// "good"; absence means print would fall back to the 800px "_m" tier,
// below the floor. Drive: Drive's own files.get metadata
// (fields=imageMediaMetadata) returns real width/height without
// downloading the file. Anything else (an arbitrary external URL from
// free-form Info HTML) isn't checked — returns null (unknown, no warning)
// rather than guessing. Returns true (low quality), false (fine), or null
// (couldn't determine).
export async function checkPhotoQuality(rawUrl) {
  const info = await photoPrintInfo(rawUrl);
  return info ? info.low : null;
}

// What the low-resolution check found (2026-10-28), so the warning can say
// it: { low, w, h, source: "drive" | "upload" | "web" } or null (unknown).
// An upload's size isn't read — only whether its ~2000px "_l" copy exists
// (w/h stay null; low means only the ~800px "_m" copy is there).
// Only "that copy doesn't exist" counts as low: any other error (no
// permission to look, network, an address that isn't Storage at all) used
// to be reported as low resolution too, warning about photos that were fine.
export async function photoPrintInfo(rawUrl) {
  if (!rawUrl) return null;
  // A photo from the web (Flickr, Wikimedia, a stock photo's printUrl):
  // measure it. It used to fall into the Firebase check below, which can't
  // read a non-Storage URL and so flagged every one of them as low quality.
  // 2026-10-17.
  if (/^https?:/i.test(rawUrl) && !/firebasestorage\.googleapis\.com/i.test(rawUrl)) {
    const size = await measureWebImage(rawUrl);
    return size ? { ...size, low: Math.min(size.w, size.h) < PRINT_MIN_PX, source: "web" } : null;
  }
  if (rawUrl.startsWith("gdrive:")) {
    const fileId = rawUrl.slice("gdrive:".length);
    const accessToken = await getDriveAccessToken();
    if (!accessToken) return null;
    try {
      const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?fields=imageMediaMetadata`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const w = data.imageMediaMetadata?.width;
      const h = data.imageMediaMetadata?.height;
      if (!w || !h) return null;
      return { low: Math.min(w, h) < PRINT_MIN_PX, w, h, source: "drive" };
    } catch (e) {
      return null;
    }
  }
  let largePath;
  try {
    const ref = firebase.storage().refFromURL(rawUrl);
    largePath = ref.fullPath.replace(/_m$/, "_l");
    if (largePath === ref.fullPath) return null; // not this pipeline's shape — unknown
  } catch (e) {
    return null; // not a Storage address — unknown
  }
  try {
    await firebase.storage().ref(largePath).getMetadata();
    return { low: false, w: null, h: null, source: "upload" }; // "_l" tier exists — good quality
  } catch (e) {
    if (e && e.code === "storage/object-not-found") return { low: true, w: null, h: null, source: "upload" }; // only the ~800px "_m" tier
    return null; // couldn't look (permission, network) — unknown, no warning
  }
}

// The low-resolution warning's detail: " (1600 × 1200 px from your Google
// Drive; 1240 needed)", " (only an 800 px copy was uploaded)" — or "".
export function lowQualityDetail(info) {
  if (!info) return "";
  const where = { drive: "from your Google Drive", web: "from the web" }[info.source];
  if (info.w && info.h) return ` (${info.w} × ${info.h} px${where ? ` ${where}` : ""}; ${PRINT_MIN_PX} needed)`;
  if (info.source === "upload") return " (only a small copy of this upload is stored, about 800 px)";
  return "";
}

// Loads the image (no download beyond what the browser does to show it):
// { w, h }, or null when it can't be loaded.
function measureWebImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(null), 15000);
    img.onload = () => { clearTimeout(timer); resolve(img.naturalWidth && img.naturalHeight ? { w: img.naturalWidth, h: img.naturalHeight } : null); };
    img.onerror = () => { clearTimeout(timer); resolve(null); };
    img.referrerPolicy = "no-referrer";
    img.src = url;
  });
}

// Custom pages (type "html", added via "+ Add Custom Page") store their
// content as freeform captionHtml from the HTMLEditor — any photo embedded
// there is a plain <img src="..."> pointing at the normal in-app url_m
// (~800px) tier, same as everywhere else a photo displays. Unlike auto-
// generated photo pages, nothing was ever upgrading these to the print-
// quality "_l" tier, nor warning about low resolution in the Preview list —
// a custom page's embedded photo silently printed softer than the rest of
// the book. These two helpers extend resolvePageImageSrc/checkPhotoQuality's
// existing per-photo logic to every <img> found in a page's captionHtml
// instead of a single page.rawUrl. Found/fixed 2026-10-04.
//
// Regex, not a DOM parser — captionHtml is always this app's own editor
// output (TinyMCE-style), not arbitrary third-party HTML, so a simple
// src="..." match is reliable and avoids pulling in a DOM parsing dependency
// for something this narrow.
function extractImgSrcs(html) {
  const srcs = [];
  const re = /<img\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/gi;
  let m;
  while ((m = re.exec(html)) !== null) srcs.push(m[1]);
  return srcs;
}

// Returns true if ANY embedded photo in this HTML page would be low
// quality, false if all embedded photos (if any) check out fine, or null if
// there are no photos to check. Mirrors checkPhotoQuality's own
// true/false/null contract.
export async function checkHtmlPhotoQuality(html) {
  if (!html) return null;
  const srcs = extractImgSrcs(html);
  if (srcs.length === 0) return null;
  const results = await Promise.all(srcs.map((src) => checkPhotoQuality(src)));
  if (results.some((r) => r === true)) return true;
  if (results.every((r) => r === null)) return null;
  return false;
}

// What the low-resolution check looks at for one Preview page (2026-10-29): a photo page's photo,
// the back cover's own photo — its page HTML holds only the small screen copy, so every Google
// Drive or web photo on the back cover was reported as low resolution, with no reason given —
// and a custom page's embedded photos. Resolves to photoPrintInfo's { low, w, h, source } or
// { low } or null. check = photoPrintInfo (a parameter so tests can pass their own).
export function pagePrintCheck(page, check = photoPrintInfo) {
  if (!page) return null;
  if (page.isBackCover) return page.backPhoto ? check(page.backPhoto.printUrl || page.backPhoto.rawUrl) : null;
  if (page.type === "photo" && page.rawUrl) return check(page.printUrl || page.rawUrl);
  if (page.type === "html" && page.captionHtml) return checkHtmlPhotoQuality(page.captionHtml).then((low) => (low ? { low } : null));
  return null;
}

// Rewrites every <img src="..."> in a page's captionHtml to its best
// available print-quality source, same resolution logic resolvePageImageSrc
// already applies to a single photo page. A src that fails to resolve (or
// isn't one of the known pipelines) is left as-is rather than breaking the
// page — print at the original resolution beats dropping the image.
async function upgradeEmbeddedImages(html, uid, guideId, pageIndex) {
  if (!html) return html;
  const srcs = extractImgSrcs(html);
  if (srcs.length === 0) return html;
  let result = html;
  for (let i = 0; i < srcs.length; i++) {
    const original = srcs[i];
    try {
      const resolved = await resolvePageImageSrc(original);
      if (!resolved || resolved === original) continue;
      const finalUrl = resolved.startsWith("blob:") ? await uploadTempBlob(resolved, uid, guideId, `${pageIndex}_${i}`) : resolved;
      if (finalUrl) result = result.split(original).join(finalUrl);
    } catch (e) {
      // leave this particular <img> at its original src
    }
  }
  return result;
}

export async function resolvePageImageSrc(rawUrl) {
  if (!rawUrl) return null;
  if (!rawUrl.startsWith("gdrive:")) return resolveFirebaseLargeVariant(rawUrl);
  const fileId = rawUrl.slice("gdrive:".length);
  const accessToken = await getDriveAccessToken();
  if (!accessToken) return null;
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Screen-resolution-only resolution for the Preview list/flip-viewer —
// deliberately NOT print quality (no "_l" probe, no Drive full-file API
// fetch). Firebase-sourced photos display as rawUrl (url_m, ~800px) as-is;
// Drive-sourced photos use the app's normal in-place embed thumbnail
// endpoint (Common.getImageURL's `lh3.googleusercontent.com/d/{fileId}`,
// the same one used everywhere else a photo shows up in the app) instead
// of authenticating and downloading the real file. Two benefits at once:
// faster (no network round trip beyond loading the image itself, no Drive
// auth needed just to browse), and the full print-resolution file is never
// fetched or displayed until an actual order is started via
// resolveAllPagesForPdf below — closing the "save the preview and print it
// elsewhere" gap a watermark would only have papered over. Synchronous;
// no async/await needed unlike resolvePageImageSrc.
// A Wikimedia Commons original as Wikimedia's own 1280px thumbnail, for
// showing on screen (2026-10-17). Originals are often 3-10 MB; Android's
// image loader gave up on the larger ones (blank pages in the preview) and
// they were slow everywhere. Commons only serves a fixed set of widths
// (960, 1280… — 1024 and 800 return 400). JPEG/PNG/GIF/WebP only; anything
// else, or a URL that isn't a Commons original, is returned as is. The
// original stays for printing (resolvePageImageSrc / printUrl).
export function commonsDisplayUrl(url, width = 1280) {
  const m = /^https?:\/\/upload\.wikimedia\.org\/wikipedia\/commons\/([0-9a-f])\/([0-9a-f]{2})\/([^/?#]+)/i.exec(String(url || ""));
  if (!m || !/\.(jpe?g|png|gif|webp)$/i.test(m[3])) return url;
  return `https://upload.wikimedia.org/wikipedia/commons/thumb/${m[1]}/${m[2]}/${m[3]}/${width}px-${m[3]}`;
}

export function resolvePreviewImageSrc(rawUrl) {
  if (!rawUrl) return null;
  if (rawUrl.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${rawUrl.slice(7)}`;
  return commonsDisplayUrl(rawUrl);
}

// Phase B1 (project plan): the Cloud Function that renders the actual PDF
// only accepts plain, already-fetchable image URLs — no Google Drive auth,
// no Firebase Storage _l-variant probing server-side, both of which belong
// on the client where they already work (resolvePageImageSrc above). A
// Drive-sourced page resolves to a blob: URL (this browser session only,
// not fetchable by anything else), so it needs one more step: upload that
// blob to a temporary Storage path and use its real download URL instead.
// Firebase-sourced pages already resolve to a plain URL and skip this.
async function uploadTempBlob(blobUrl, uid, guideId, index) {
  const blob = await (await fetch(blobUrl)).blob();
  const path = `photobook_temp/${uid || "anon"}/${guideId}/${index}.jpg`;
  const ref = firebase.storage().ref(path);
  await ref.put(blob, { contentType: "image/jpeg" });
  return ref.getDownloadURL();
}

// Resolves every page to { placeName, imageUrl, objectPosition } — the
// flat, uniform contract the Cloud Function expects. A map page's imageUrl
// is already a plain fetchable Google Static Maps URL (no Drive/Firebase
// resolution needed, unlike a photo page's rawUrl), so it passes straight
// through; its caption maps to the same placeName field a photo page's
// place name uses, so buildBookHtml needs no type-specific handling. Call
// this right before generating a PDF, not at preview time (the preview
// screen already resolves images one at a time for display; this
// re-resolves because a temp upload is only worth doing when a PDF is
// actually about to be generated).
// Why the book can't be made (2026-11-02): a page whose photo or map couldn't be loaded used to be dropped
// without a word — a Japan book of 88 photos printed as 38 pages, its Google Drive photos missing (the app had
// no Drive access on that phone) and the odd count turned the back cover white. Now nothing is made; this says
// which pages and what to do.
export function unloadedPagesMessage(missing, driveMissing = 0) {
  const n = missing.length;
  const names = missing.slice(0, 5).join(", ") + (n > 5 ? ` and ${n - 5} more` : "");
  return `${n} page${n > 1 ? "s" : ""} couldn't be loaded, so the book wasn't made (nothing was ordered or charged): ${names}.`
    + (driveMissing
      ? ` ${driveMissing === n ? (n > 1 ? "They are" : "It is") : `${driveMissing} of them ${driveMissing > 1 ? "are" : "is"}`} in Google Drive: connect Google Drive on this device (add one photo from Google Drive) and try again.`
      : " Check your internet connection and try again, or remove those pages.");
}

export async function resolveAllPagesForPdf(pages, uid, guideId) {
  const resolved = [];
  const missing = [];
  let driveMissing = 0;
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    // A blank page (the "Blank cover" option, a blank page added inside):
    // was skipped here — it fell through to the photo branch, found no
    // image and was dropped, so the printed book had neither. buildBookHtml
    // (functions/index.js) prints { blank: true } as an empty page.
    // 2026-10-17.
    if (page.type === "blank") {
      resolved.push({ blank: true });
      continue;
    }
    if (page.isBackCover) {
      // The back cover's photo at print quality, like a photo page's.
      let photoUrl = "";
      if (page.backPhoto) {
        const src = page.backPhoto.printUrl || (await resolvePageImageSrc(page.backPhoto.rawUrl));
        if (src) photoUrl = src.startsWith("blob:") ? await uploadTempBlob(src, uid, guideId, `${i}_back`) : src;
        else {
          missing.push("the back cover photo");
          if (String(page.backPhoto.rawUrl || "").startsWith("gdrive:")) driveMissing++;
        }
      }
      resolved.push({ type: "html", placeName: page.title || "", captionHtml: buildBackCoverHtml({ photoUrl, text: page.backText, madeWith: page.madeWith }) });
      continue;
    }
    if (page.type === "html") {
      // buildBookHtml renders captionHtml as the full page instead of a
      // small overlay. title doubles as placeName purely for the confirm-
      // before-generate dialog's benefit (not shown as a caption on top of
      // itself). Any <img> embedded in captionHtml gets the same print-
      // quality upgrade a type:"photo" page's rawUrl gets below — see
      // upgradeEmbeddedImages. Found 2026-10-04.
      const captionHtml = await upgradeEmbeddedImages(page.captionHtml || "", uid, guideId, i);
      resolved.push({ type: "html", placeName: page.title || "", captionHtml });
      continue;
    }
    if (page.type === "map") {
      if (!page.imageUrl) { missing.push(page.caption || `map, page ${i + 1}`); continue; }
      resolved.push({
        type: "map",
        placeName: page.caption || "",
        imageUrl: page.imageUrl,
        objectPosition: page.objectPosition || "center",
        captionHtml: page.captionHtml || null,
      });
      continue;
    }
    // A stock photo's print-size version when there is one (see printUrl).
    const src = page.printUrl ? page.printUrl : await resolvePageImageSrc(page.rawUrl);
    if (!src) {
      missing.push(page.place?.Name || page.title || `page ${i + 1}`);
      if (String(page.rawUrl || "").startsWith("gdrive:")) driveMissing++;
      continue;
    }
    const imageUrl = src.startsWith("blob:") ? await uploadTempBlob(src, uid, guideId, i) : src;
    resolved.push({
      type: "photo",
      placeName: page.place?.Name || "",
      imageUrl,
      objectPosition: page.objectPosition || "center",
      captionHtml: page.captionHtml || null,
    });
  }
  if (missing.length) throw new Error(unloadedPagesMessage(missing, driveMissing));
  return resolved;
}

// Calls the deployed Cloud Function (Phase B1) with an already-resolved
// page list and returns the generated PDF's download URL.
const FUNCTIONS_BASE_URL = "https://us-central1-andiamoplacesproduction.cloudfunctions.net";

export async function generatePhotoBookPdf(guideId, uid, resolvedPages) {
  const res = await fetch(`${FUNCTIONS_BASE_URL}/generatePhotoBookPdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guideId, uid, pages: resolvedPages }),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "generatePhotoBookPdf failed");
  // coverUrl: a picture of the printed front cover, for Peecho's checkout
  // thumbnail (2026-10-19); null from an older function or on failure.
  // proofUrls: a small picture of every printed page, for the Final check.
  return { url: data.url, coverUrl: data.coverUrl || null, proofUrls: data.proofUrls || null };
}

// Hands the already-generated PDF (its public photobook_pdfs/ URL, from
// generatePhotoBookPdf above) to Peecho's Publication/Print Button API —
// the hosted-checkout flow where the END CUSTOMER pays Peecho directly
// (not a custom Stripe integration, per the project plan). Returns a real
// checkout URL on Peecho's sandbox/production host to redirect to.
export async function createPeechoCheckout(guideId, pdfUrl, pageCount, title, thumbnailUrl) {
  const res = await fetch(`${FUNCTIONS_BASE_URL}/createPeechoPublication`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ guideId, pdfUrl, pageCount, title, thumbnailUrl }),
  });
  const data = await res.json();
  if (!data.ok) throw new Error(data.error || "createPeechoPublication failed");
  return data.checkoutUrl;
}

// Real price for the confirm-before-generate step (project plan's "Real
// per-page pricing + page-count caps" item) — replaces a plain page count
// with the actual Peecho PRODUCT price (base + per-page) for our specific
// offering. Deliberately does NOT include shipping/tax — Peecho's own API
// only exposes a wholesale shipping estimate there, not the real retail
// rate the checkout actually charges (confirmed ~3x apart in a real test
// order 2026-10-01) — so callers should say shipping/tax are determined at
// checkout rather than present a misleadingly-precise total. Returns null
// on failure (e.g. page count outside the offering's min/max) so callers
// can fall back to the plain count-only confirmation rather than blocking
// the whole flow on a pricing lookup.
export async function getPeechoQuote(pageCount) {
  try {
    const res = await fetch(`${FUNCTIONS_BASE_URL}/getPeechoQuote?pageCount=${pageCount}`);
    const data = await res.json();
    if (!data.ok) return null;
    return data;
  } catch (e) {
    return null;
  }
}
